import {div, filler, hbox, span} from '@xh/hoist/cmp/layout';
import {ContextMenuSpec, hoistCmp, HoistProps} from '@xh/hoist/core';
import {button} from '@xh/hoist/desktop/cmp/button';
import {jsonInput, select, switchInput, SwitchInputProps, textInput} from '@xh/hoist/desktop/cmp/input';
import {panel, PanelProps} from '@xh/hoist/desktop/cmp/panel';
import {useContextMenu} from '@xh/hoist/desktop/hooks';
import {Icon} from '@xh/hoist/icon';
import {castArray, isNil} from 'lodash';
import {ReactElement, useState} from 'react';
import {SpecModel} from '../../spec/SpecModel';
import {StyleCondition} from '../GridSpecTypes';
import {SelectOption} from './GridSpecConfiguratorModel';
import './Common.scss';

interface ConfigPanelItemProps extends HoistProps {
    icon?: ReactElement;
    label?: string;
    omitFiller?: boolean;
    contextMenu?: ContextMenuSpec;
}

export const configPanelItem = hoistCmp.factory<ConfigPanelItemProps>({
    className: 'config-panel-item',
    model: false,
    render({className, icon, label, omitFiller, children, contextMenu, ...rest}, ref) {
        children = castArray(children);
        const ret = hbox({
            ref,
            className,
            ...rest,
            items: [
                div({className: 'config-panel-item__icon', omit: !icon, item: icon}),
                label ? span({className: 'config-panel-item__label', item: label}) : null,
                label && !omitFiller ? filler() : null,
                ...children
            ]
        });

        return contextMenu ? useContextMenu(ret, contextMenu) : ret;
    }
});

export const configPanelSwitch = hoistCmp.factory<SwitchInputProps>({
    model: false,
    render: props =>
        switchInput({
            className: 'config-panel-item__switch',
            labelSide: 'left',
            ...props
        })
});

export const stackedPanel = hoistCmp.factory<PanelProps>({
    className: 'stacked-panel',
    model: false,
    render({className, ...rest}) {
        return panel({
            className,
            compactHeader: true,
            modelConfig: {
                side: 'top',
                defaultSize: 'fit-content',
                resizable: false,
                showSplitter: false
            },
            ...rest
        });
    }
});

export const OPERATOR_OPTIONS: SelectOption[] = [
    {value: '=', label: '='},
    {value: '!=', label: '≠'},
    {value: '>', label: '>'},
    {value: '>=', label: '≥'},
    {value: '<', label: '<'},
    {value: '<=', label: '≤'},
    {value: 'like', label: 'like'},
    {value: 'not like', label: 'not like'},
    {value: 'begins', label: 'begins'},
    {value: 'ends', label: 'ends'}
];

interface ConditionRowProps extends HoistProps {
    condition: StyleCondition;
    fieldOptions: SelectOption[];
    onChange: (changes: Partial<StyleCondition>) => void;
    /** Omit to hide the remove button. */
    onRemove?: () => void;
    /** Extra item shown before the remove button (e.g. a style picker). */
    extra?: ReactElement;
}

/** Editor row for a field / operator / value condition - used for filters and style rules. */
export const conditionRow = hoistCmp.factory<ConditionRowProps>({
    model: false,
    render({condition, fieldOptions, onChange, onRemove, extra}) {
        return configPanelItem({
            className: 'config-panel-item config-panel-item--condition',
            omitFiller: true,
            items: [
                select({
                    value: condition.field,
                    onChange: field => onChange({field}),
                    options: fieldOptions,
                    placeholder: 'Field',
                    flex: 1,
                    width: null
                }),
                select({
                    value: condition.op,
                    onChange: op => onChange({op}),
                    options: OPERATOR_OPTIONS,
                    enableFilter: false,
                    width: 70
                }),
                textInput({
                    // textInput shows nothing for a number - e.g. the 0 in a new rule
                    value: isNil(condition.value) ? null : String(condition.value),
                    onChange: value => onChange({value}),
                    commitOnChange: true,
                    placeholder: 'Value',
                    width: 70
                }),
                extra,
                onRemove ? button({icon: Icon.x(), intent: 'danger', minimal: true, onClick: onRemove}) : null
            ]
        });
    }
});

export const sectionAddButton = (onClick: () => void, title: string, disabled: boolean = false) =>
    button({icon: Icon.add(), minimal: true, intent: 'success', title, disabled, onClick});

interface SpecJsonSectionProps extends HoistProps {
    specModel: SpecModel<object, any, object>;
}

/** Advanced section of a spec configurator - the spec itself as JSON, applied with the kind's replaceSpec op. */
export const specJsonSection = hoistCmp.factory<SpecJsonSectionProps>({
    model: false,
    render({specModel}) {
        const current = JSON.stringify(specModel.spec, null, 2),
            [draft, setDraft] = useState<string>(null),
            [error, setError] = useState<string>(null),
            value = draft ?? current;

        const apply = () => {
            try {
                const spec = JSON.parse(value),
                    {rejected} = specModel.dispatch([specModel.reducer.replaceOp(spec)], {source: 'manual', label: 'Edit JSON'});
                setError(rejected[0]?.reason ?? null);
                if (!rejected.length) setDraft(null);
            } catch (e) {
                setError(`Invalid JSON: ${e.message}`);
            }
        };

        return stackedPanel({
            title: 'Spec (JSON)',
            modelConfig: {side: 'top', defaultSize: 'fit-content', resizable: false, showSplitter: false, defaultCollapsed: true},
            items: [
                jsonInput({
                    value,
                    onChange: v => setDraft(v),
                    commitOnChange: true,
                    height: 260,
                    width: null,
                    flex: 'none',
                    showFullscreenButton: true
                }),
                div({className: 'gs-configurator__error', omit: !error, item: error}),
                configPanelItem({
                    omitFiller: true,
                    items: [
                        button({text: 'Reset to Default', icon: Icon.reset(), minimal: true, onClick: () => specModel.reset()}),
                        filler(),
                        button({text: 'Discard', minimal: true, disabled: draft === null, onClick: () => (setDraft(null), setError(null))}),
                        button({text: 'Apply', intent: 'primary', disabled: draft === null, onClick: apply})
                    ]
                })
            ]
        });
    }
});
